import { useForm } from '@inertiajs/react';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastFlash } from '@/lib/flash-toast';
import type { Sponsor } from '@/types';

interface EditSponsorDialogProps {
  eventSlug: string;
  sponsor: Sponsor;
}

export function EditSponsorDialog({
  eventSlug,
  sponsor,
}: EditSponsorDialogProps) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    slug: sponsor.slug,
    name: sponsor.name,
    url: sponsor.url ?? '',
    logoUrl: sponsor.logoUrl ?? '',
  });
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.patch(`/admin/${eventSlug}/sponsors/${sponsor.id}`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Sponsor updated successfully!');
        setOpen(false);
      },
    });
  };

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
        <Pencil className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Sponsor</DialogTitle>
            <DialogDescription>
              Update information for {sponsor.name}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor={`edit-sponsor-slug-${sponsor.id}`}>Slug</Label>
              <Input
                id={`edit-sponsor-slug-${sponsor.id}`}
                disabled={processing}
                value={data.slug}
                onChange={(e) => setData('slug', e.target.value)}
              />
              {errors.slug && (
                <p className="text-destructive text-sm">{errors.slug}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-sponsor-name-${sponsor.id}`}>Name</Label>
              <Input
                id={`edit-sponsor-name-${sponsor.id}`}
                disabled={processing}
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
              />
              {errors.name && (
                <p className="text-destructive text-sm">{errors.name}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-sponsor-url-${sponsor.id}`}>
                URL (Optional)
              </Label>
              <Input
                id={`edit-sponsor-url-${sponsor.id}`}
                disabled={processing}
                value={data.url}
                onChange={(e) => setData('url', e.target.value)}
              />
              {errors.url && (
                <p className="text-destructive text-sm">{errors.url}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-sponsor-logo-url-${sponsor.id}`}>
                Logo URL (Optional)
              </Label>
              <Input
                id={`edit-sponsor-logo-url-${sponsor.id}`}
                disabled={processing}
                value={data.logoUrl}
                onChange={(e) => setData('logoUrl', e.target.value)}
              />
              {errors.logoUrl && (
                <p className="text-destructive text-sm">{errors.logoUrl}</p>
              )}
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
